import { useQuery } from '@apollo/client/react';
import { Link } from 'react-router-dom';

import { API_KEYS } from '@/account/api-keys';
import { ApiKeyList } from '@/components/ApiKeyList/ApiKeyList';
import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { SettingsHeading } from '@/components/SettingsHeading/SettingsHeading';
import { Spinner } from '@/components/Spinner/Spinner';

import { useApiKeyActions } from './useApiKeyActions';

/** The organization's API keys, and creating, regenerating and revoking them. */
export function ApiKeysContainer() {
  const { data, loading, error, refetch } = useQuery(API_KEYS);
  const actions = useApiKeyActions();
  const heading = (
    <SettingsHeading
      title="API Keys"
      description="Keys authenticate your requests to the LendWell API. Keep them secret — anyone holding one can spend your credits."
      actions={
        <Button onClick={actions.openCreate} disabled={actions.limitReached} icon="+">
          Create API Key
        </Button>
      }
    />
  );

  if (loading && !data) {
    return (
      <>
        {heading}
        <Spinner />
      </>
    );
  }

  if (error && !data) {
    return (
      <Callout tone="danger" title="Could not load your API Keys">
        <Button variant="ghost" onClick={() => void refetch()}>
          Try again
        </Button>
      </Callout>
    );
  }

  return (
    <>
      {heading}
      <ApiKeyList
        notice={
          actions.limitReached ? (
            <Callout title="Key limit reached">
              You have the most active keys allowed. Revoke one to create another.
            </Callout>
          ) : null
        }
        keys={data?.apiKeys ?? []}
        onRegenerate={actions.openRegenerate}
        onRevoke={actions.openRevoke}
        renderName={(key) => (
          <Link to={`/settings/api-keys/${key.id}`}>{key.name || 'Untitled key'}</Link>
        )}
      />
      {actions.dialog}
    </>
  );
}
